import { html, nothing, type PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { BasePage } from '@vitesse/base-ui/components/base-page';
// The gate reads the SAME annotation the api-gateway enforces.
import { Plugins } from '@vitesse/proto/vitesse/v1/command_pb.js';
import { Integrations } from '@vitesse/proto/vitesse/v1/integrations_pb.js';
import { Dashboards } from '@vitesse/proto/vitesse/v1/dashboard_pb.js';
import type { ControllerManager, ConnState } from '../managers/controller-manager';
import type { CameraManager } from '../managers/camera-manager';
import type { CameraStreamManager } from '../components/camera/camera-stream-manager';
import type { ModelManager } from '../managers/model-manager';
import type { IntegrationManager } from '../managers/integration-manager';
import { connViaLabel, type TransportManager, type TransportInfo } from '../managers/transport-manager';
import type { Controller } from '../../core/domain/controller';
import type { Camera } from '../../core/domain/camera';
import type { ModelView } from '../../core/domain/model';
import type { Integration } from '../../core/domain/integration';
import type { BackendClient } from '../bridge/backend-client';
import type { StreamFrame } from '../../core/bridge/types';
import type { DescriptionRow } from '../../workers/backend.worker';
import { statusBadge, statusColor, statusText, baseGrid, type PillTab } from '@vitesse/base-ui/components/templates';
import { gatewayNetBadges, type GatewayNetwork } from './gateway-net-badges';
import { button } from '@vitesse/base-ui/components/base-buttons';
import { gatewayTabs } from './gateway-tabs';
import '../components/dashboard/dashboard-card';
import type { DashboardManager } from '../managers/dashboard-manager';
import type { DashboardSummary } from '../../core/domain/dashboard';
import { modelOverviewCard } from '../components/model/model-card';
import '../components/integration/integration-card';
import '../components/controller/controller-card';
import '../components/camera/camera-card';
import { t } from '@vitesse/base-ui/i18n';
import { getSession } from '@vitesse/base-ui';
import { UserData } from '@vitesse/proto/vitesse/v1/user_data_pb.js';
import '@vitesse/base-ui/elements/vitesse-editable-description';
import type { PluginTypesManager } from '../managers/plugin-types-manager';
import type { ConnectionManager } from '../managers/connection-manager';
import { dataStreams, deriveConnState } from '../managers/conn-state';
import type { PluginType, FleetGateway } from '../../core/bridge/connect-client';
import './vitesse-add-controller-dialog';
import './vitesse-add-integration-dialog';
import './vitesse-create-dashboard-dialog';
import { rooms, everyKindFull, firstFull, LIMIT_KEY } from '../components/integration/integration-limits';

/*
 * <vitesse-gateway-detail> — React→Lit port of the agent's GatewayDetailPage,
 * LIGHT DOM with the agent's base-page.scss markup. Back button → home; title;
 * pill tabs (Controllers/Models/Cameras/Dashboards) over a BaseGrid of the
 * agent's overview cards. Controllers carry data; the other tabs scaffold empty.
 */
type Tab = 'all' | 'controllers' | 'integrations' | 'models' | 'cameras' | 'dashboards';

@customElement('vitesse-gateway-detail')
export class GatewayDetail extends BasePage {
  @property({ attribute: false }) controllerManager?: ControllerManager;
  @property({ attribute: false }) cameraManager?: CameraManager;
  /** The app-global media plane (shared PCs/streams), threaded into every feed. */
  @property({ attribute: false }) cameraStreams?: CameraStreamManager;
  @property({ attribute: false }) modelManager?: ModelManager;
  @property({ attribute: false }) integrationManager?: IntegrationManager;
  @property({ attribute: false }) transport?: TransportManager;
  @property({ attribute: false }) pluginTypesManager?: PluginTypesManager;
  /** This box's boards. The page showed a hardcoded zero without it. */
  @property({ attribute: false }) dashboardManager?: DashboardManager;
  @property({ attribute: false }) backend?: BackendClient;
  /** The roster entry — its status + lastSeen drive the cloud-leg presence
   *  downgrade (same as the home card). */
  @property({ attribute: false }) gateway?: FleetGateway;
  /** The frontend connect/disconnect intent (the Connect/Disconnect/Cancel
   *  control in the page header). */
  @property({ attribute: false }) connection?: ConnectionManager;
  @property() gatewayId = '';
  /** Friendly label (displayName||hostname||fingerprint) — matches the tree node
   *  so the header isn't a raw fingerprint while the tree shows a name. */
  @property() gatewayName = '';
  /** The box's per-NIC IPs (IT/OT/WiFi/public) — drives the network badges. */
  @property({ attribute: false }) network?: GatewayNetwork;
  @state() private tab: Tab = 'all';
  @state() private controllers: Controller[] = [];
  @state() private cameras: Camera[] = [];
  @state() private connVia?: TransportInfo; // LAN vs cloud path + RTT
  @state() private models: ModelView[] = [];
  @state() private integrations: Integration[] = [];
  @state() private pluginTypes: PluginType[] = [];
  @state() private dashboards: DashboardSummary[] = [];
  @state() private addOpen = false;
  @state() private addIntegrationOpen = false;
  @state() private createDashboardOpen = false;
  // Per-controller descriptions (user_data) — shown on the cards in place of the
  // raw protocol, the same way model cards show their description.
  @state() private descriptions = new Map<string, string>();
  private descPort?: MessagePort;
  private descSubscribed = false;
  /** The gateway's OWN description (user_data), live. */
  @state() private gatewayDescription = '';
  private gwDescPort?: MessagePort;

  private readonly onChange = () => {
    this.controllers = this.controllerManager?.all ?? [];
  };
  private readonly onCamerasChange = () => {
    this.cameras = this.cameraManager?.all ?? [];
  };
  private readonly onModelsChange = () => {
    this.models = this.modelManager?.all ?? [];
  };
  private readonly onIntegrationsChange = () => {
    this.integrations = this.integrationManager?.all ?? [];
  };
  private readonly onTransportChange = () => {
    this.connVia = this.transport?.transportInfo;
  };
  private readonly onPluginTypesChange = () => {
    this.pluginTypes = this.pluginTypesManager?.all ?? [];
  };
  private readonly onDashboardsChange = () => {
    this.dashboards = this.dashboardManager?.all ?? [];
  };
  // Intent flip (Connect/Disconnect/Cancel) → re-evaluate the badge + the control.
  private readonly onConnChange = () => {
    this.requestUpdate();
  };
  connectedCallback(): void {
    super.connectedCallback();
    this.controllerManager?.addEventListener('change', this.onChange);
    this.cameraManager?.addEventListener('change', this.onCamerasChange);
    this.modelManager?.addEventListener('change', this.onModelsChange);
    this.integrationManager?.addEventListener('change', this.onIntegrationsChange);
    this.transport?.addEventListener('change', this.onTransportChange);
    this.pluginTypesManager?.addEventListener('change', this.onPluginTypesChange);
    this.dashboardManager?.addEventListener('change', this.onDashboardsChange);
    this.connection?.addEventListener('change', this.onConnChange);
    this.onChange();
    this.onCamerasChange();
    this.onModelsChange();
    this.onIntegrationsChange();
    this.onTransportChange();
    this.onPluginTypesChange();
    this.onDashboardsChange();
    void this.openDescriptions();
  }
  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.controllerManager?.removeEventListener('change', this.onChange);
    this.cameraManager?.removeEventListener('change', this.onCamerasChange);
    this.modelManager?.removeEventListener('change', this.onModelsChange);
    this.integrationManager?.removeEventListener('change', this.onIntegrationsChange);
    this.transport?.removeEventListener('change', this.onTransportChange);
    this.pluginTypesManager?.removeEventListener('change', this.onPluginTypesChange);
    this.dashboardManager?.removeEventListener('change', this.onDashboardsChange);
    this.connection?.removeEventListener('change', this.onConnChange);
    this.closeDescriptions();
  }

  // Per-controller descriptions stream (user_data), keyed by gateway — opened on
  // enter + re-opened on gateway swap; the snapshot frame carries the whole
  // (tiny) set, which the cards render.
  private async openDescriptions(): Promise<void> {
    if (this.descSubscribed || !this.backend || !this.gatewayId) return;
    this.descSubscribed = true;
    const port = await this.backend.api.subscribeControllerDescriptions(this.gatewayId);
    if (!this.isConnected) {
      port.close();
      this.descSubscribed = false;
      return;
    }
    this.descPort = port;
    port.onmessage = (e: MessageEvent<StreamFrame<DescriptionRow>>) => {
      const frame = e.data;
      if (frame.kind !== 'snapshot') return;
      this.descriptions = new Map(frame.items.map((d) => [d.controllerId, d.description]));
    };
  }
  private closeDescriptions(): void {
    this.descPort?.postMessage({ kind: 'close' });
    this.descPort?.close();
    this.descPort = undefined;
    this.descSubscribed = false;
    this.gwDescPort?.postMessage({ kind: 'close' });
    this.gwDescPort?.close();
    this.gwDescPort = undefined;
  }

  // The gateway's OWN description (a single user_data key), opened alongside the
  // per-controller descriptions and re-opened on gateway swap.
  private async openGatewayDescription(): Promise<void> {
    if (this.gwDescPort || !this.backend || !this.gatewayId) return;
    const port = await this.backend.api.subscribeGatewayDescription(this.gatewayId);
    if (!this.isConnected) {
      port.close();
      return;
    }
    this.gwDescPort = port;
    port.onmessage = (e: MessageEvent<StreamFrame<{ description: string }>>) => {
      const frame = e.data;
      if (frame.kind === 'snapshot') this.gatewayDescription = frame.items[0]?.description ?? '';
    };
  }

  private commitGatewayDescription(next: string): void {
    if (!this.backend) return;
    void this.backend.api.putGatewayDescription(this.gatewayId, next).catch(() => {
      /* transient write failure — the live stream stays the source of truth */
    });
  }

  // The shell REUSES this element across gateway→gateway navigation, swapping the
  // managers (+ gatewayId/Name) without re-running connectedCallback. Re-bind the
  // listeners to the NEW gateway's managers (dropping the previous gateway's) and
  // re-read immediately, so the cards reflect the active gateway instead of stale
  // cards from the one we navigated away from. (addEventListener dedupes by
  // function reference, so the connectedCallback subscribe is never doubled.)
  protected willUpdate(changed: PropertyValues): void {
    if (changed.has('controllerManager')) {
      (changed.get('controllerManager') as ControllerManager | undefined)?.removeEventListener('change', this.onChange);
      this.controllerManager?.addEventListener('change', this.onChange);
      this.onChange();
    }
    if (changed.has('cameraManager')) {
      (changed.get('cameraManager') as CameraManager | undefined)?.removeEventListener('change', this.onCamerasChange);
      this.cameraManager?.addEventListener('change', this.onCamerasChange);
      this.onCamerasChange();
    }
    if (changed.has('modelManager')) {
      (changed.get('modelManager') as ModelManager | undefined)?.removeEventListener('change', this.onModelsChange);
      this.modelManager?.addEventListener('change', this.onModelsChange);
      this.onModelsChange();
    }
    // The manager arrives as a PROPERTY after construction and is replaced when
    // the page is reused for a different gateway, so rebinding here is what keeps
    // the count honest — connectedCallback alone would bind to undefined once and
    // the tab would read zero forever, which is the defect this whole change fixes.
    if (changed.has('dashboardManager')) {
      (changed.get('dashboardManager') as DashboardManager | undefined)?.removeEventListener('change', this.onDashboardsChange);
      this.dashboardManager?.addEventListener('change', this.onDashboardsChange);
      this.onDashboardsChange();
    }
    if (changed.has('integrationManager')) {
      (changed.get('integrationManager') as IntegrationManager | undefined)?.removeEventListener('change', this.onIntegrationsChange);
      this.integrationManager?.addEventListener('change', this.onIntegrationsChange);
      this.onIntegrationsChange();
    }
    if (changed.has('transport')) {
      (changed.get('transport') as TransportManager | undefined)?.removeEventListener('change', this.onTransportChange);
      this.transport?.addEventListener('change', this.onTransportChange);
      this.onTransportChange();
    }
    if (changed.has('pluginTypesManager')) {
      (changed.get('pluginTypesManager') as PluginTypesManager | undefined)?.removeEventListener('change', this.onPluginTypesChange);
      this.pluginTypesManager?.addEventListener('change', this.onPluginTypesChange);
      this.onPluginTypesChange();
    }
    if (changed.has('connection')) {
      (changed.get('connection') as ConnectionManager | undefined)?.removeEventListener('change', this.onConnChange);
      this.connection?.addEventListener('change', this.onConnChange);
    }
    // Gateway swap (or late backend) → drop the old descriptions + re-subscribe.
    if (changed.has('gatewayId') || changed.has('backend')) {
      this.closeDescriptions();
      this.descriptions = new Map();
      this.gatewayDescription = '';
      void this.openDescriptions();
      void this.openGatewayDescription();
    }
  }

  private nav(detail: { view: 'home' | 'controller' | 'model' | 'dashboard'; gatewayId?: string; controllerId?: string; modelId?: string; dashboardId?: string }): void {
    this.dispatchEvent(new CustomEvent('navigate', { detail, bubbles: true, composed: true }));
  }

  protected tabs(): PillTab[] {
    // Pure function of the counts — extracted so the counts are testable
    // without a DOM, after one tab shipped a hardcoded zero (see gateway-tabs.ts).
    return gatewayTabs({
      controllers: this.controllers.length,
      models: this.models.length,
      integrations: this.integrations.length,
      cameras: this.cameras.length,
      dashboards: this.dashboards.length,
    });
  }
  protected activeTabId(): string {
    return this.tab;
  }
  protected onTabChange(id: string): void {
    this.tab = id as Tab;
  }

  // The ONE controller card — same element as the home grid, so a card shows the
  // identical data + delete affordance wherever it's seen (own nav, prefetch, and
  // confirm-delete flow live inside it).
  private controllerCard(c: Controller) {
    return html`<vitesse-controller-card
      .controller=${c}
      .gatewayId=${this.gatewayId}
      .description=${this.descriptions.get(c.id) ?? ''}
      .pluginTypes=${this.pluginTypes}
      .backend=${this.backend}
    ></vitesse-controller-card>`;
  }

  // Hover a model card → warm its SCXML source so the detail page (statechart +
  // editor) opens instantly.
  private modelCard(m: ModelView) {
    return modelOverviewCard(m, {
      onClick: () => this.nav({ view: 'model', gatewayId: this.gatewayId, modelId: m.id }),
      onHover: () => this.modelManager?.prefetchSource(m.id),
    });
  }

  private dashboardCard(d: DashboardSummary) {
    // No gatewayName: every card on this page belongs to this box already.
    return html`<vitesse-dashboard-card
      .summary=${d}
      .manager=${this.dashboardManager}
      .gatewayId=${this.gatewayId}
      .connState=${this.eff()}
    ></vitesse-dashboard-card>`;
  }


  // The ONE camera card — same element wherever a camera shows. Its preview IS the
  // live feed (visible-gated, shared, self-healing via CameraStreamManager).
  private cameraCard(c: Camera) {
    return html`<vitesse-camera-card
      .camera=${c}
      .gatewayId=${this.gatewayId}
      .cameraStreams=${this.cameraStreams}
      ?poster=${this.transport?.transportInfo.path === 'cloud'}
    ></vitesse-camera-card>`;
  }

  private integrationCard(i: Integration) {
    // Self-contained card (owns its confirm-delete flow → Integrations.Delete).
    // Detail-page navigation lands with the schema tree.
    return html`<vitesse-integration-card
      .integration=${i}
      .gatewayId=${this.gatewayId}
      .backend=${this.backend}
    ></vitesse-integration-card>`;
  }

  /** The shared tri-state (intent + data-plane liveness + cloud presence) — same
   *  derivation as the home card + tree. Falls back to the raw controller state
   *  until all inputs are wired. */
  private eff(): ConnState {
    if (this.controllerManager && this.transport && this.gateway && this.connection) {
      return deriveConnState(
        this.controllerManager,
        this.transport,
        this.gateway,
        this.connection,
        dataStreams({ dashboards: this.dashboardManager, models: this.modelManager, integrations: this.integrationManager }),
      );
    }
    return this.controllerManager?.connectionState ?? 'connecting';
  }

  // The Connect/Disconnect/Cancel control (page header) — same 3-state spelling as
  // the home cards: disconnected → blue Connect (plug-zap); connecting → yellow
  // Cancel (refresh-cw-off); connected → red-on-hover Disconnect (unplug).
  // Frontend-only — detaches OUR streams; the box stays connected to the cloud.
  private connButton() {
    const conn = this.connection;
    if (!conn) return nothing;
    const st = this.eff();
    const onClick = () => (conn.intent === 'disconnected' ? conn.connect() : conn.disconnect());
    const o =
      st === 'disconnected'
        ? { label: t('gateway.connect'), icon: 'plug-zap', color: 'blue' as const }
        : // 'offline' takes 'connecting''s control (see vitesse-home.connButton): the
          // box is off but our intent is still connected and supervise() is retrying,
          // so the honest action is "stop trying". The fallthrough's red "Disconnect"
          // would claim we are attached to a box that is switched off.
          st === 'connecting' || st === 'offline'
          ? { label: t('gateway.cancel'), icon: 'refresh-cw-off', color: 'yellow' as const }
          : { label: t('gateway.disconnect'), icon: 'unplug', color: 'red' as const };
    return button({ ...o, onClick });
  }

  private content() {
    const st = this.eff();
    if (st !== 'connected') {
      // Not attached → hide every sub-entity; only the page header (badges + the
      // Connect control) shows. Reconnect to view controllers/models.
      // An offline box gets its OWN hint: "reconnecting…" is misleading when the
      // answer is that the box is powered off, and the disconnected hint would blame
      // a choice the user never made.
      const hint =
        st === 'offline'
          ? t('gateway.offlineHint')
          : st === 'connecting'
            ? t('gateway.connectingHint')
            : t('gateway.disconnectedHint');
      return html`<div class="home-empty-gateways"><span class="home-empty-hint">${hint}</span></div>`;
    }
    if (this.tab === 'all') {
      // Everything this gateway holds, one grid. Mirrors the home "All".
      const items = [
        ...this.controllers.map((c) => this.controllerCard(c)),
        ...this.cameras.map((c) => this.cameraCard(c)),
        ...this.models.map((m) => this.modelCard(m)),
        ...this.integrations.map((i) => this.integrationCard(i)),
        ...this.dashboards.map((d) => this.dashboardCard(d)),
      ];
      return items.length > 0
        ? baseGrid(items, 3)
        : html`<div class="home-empty-gateways"><span class="home-empty-hint">${t('home.emptyAll')}</span></div>`;
    }
    if (this.tab === 'controllers') {
      return this.controllers.length > 0
        ? baseGrid(this.controllers.map((c) => this.controllerCard(c)), 3)
        : html`<div class="home-empty-gateways"><span class="home-empty-hint">${t('gateway.noPlcs')}</span></div>`;
    }
    if (this.tab === 'models') {
      return this.models.length > 0
        ? baseGrid(this.models.map((m) => this.modelCard(m)), 3)
        : html`<div class="home-empty-gateways"><span class="home-empty-hint">${t('gateway.emptyTab', { tab: t('nav.models') })}</span></div>`;
    }
    if (this.tab === 'integrations') {
      return this.integrations.length > 0
        ? baseGrid(this.integrations.map((i) => this.integrationCard(i)), 3)
        : html`<div class="home-empty-gateways"><span class="home-empty-hint">${t('gateway.emptyTab', { tab: t('nav.integrations') })}</span></div>`;
    }
    if (this.tab === 'dashboards') {
      return this.dashboards.length > 0
        ? baseGrid(this.dashboards.map((d) => this.dashboardCard(d)), 3)
        : html`<div class="home-empty-gateways"><span class="home-empty-hint">${t('dashboards.emptyGatewayHint')}</span></div>`;
    }
    if (this.tab === 'cameras') {
      return this.cameras.length > 0
        ? baseGrid(this.cameras.map((c) => this.cameraCard(c)), 3)
        : html`<div class="home-empty-gateways"><span class="home-empty-hint">${t('gateway.emptyTab', { tab: t('nav.cameras') })}</span></div>`;
    }
    /*
     * EVERY tab now has a branch above, so this is unreachable — and the `never`
     * is what keeps that true at COMPILE time rather than by inspection. Adding a
     * seventh tab without a branch becomes a type error here instead of a page
     * that silently renders nothing, which is exactly how the dashboards tab came
     * to sit there showing a hardcoded zero.
     */
    const unhandled: never = this.tab;
    return html`<div class="home-empty-gateways"><span class="home-empty-hint">${t('gateway.emptyTab', { tab: String(unhandled) })}</span></div>`;
  }

  // ── BasePage hooks ─────────────────────────────────────────────────────────
  protected pageTitle(): string {
    return this.gatewayName || this.gatewayId;
  }
  protected titleIcon(): string {
    return 'hard-drive';
  }
  protected renderDescription() {
    // The gateway's OWN note (user_data), editable in place — same line every
    // other entity carries. The old static 'Vitesse Controller' type label moved
    // into the placeholder, so an un-described relay still reads as one.
    const canEdit = !!this.backend && getSession()?.allowsRpc(UserData.method.putGatewayDescription) !== false;
    return html`<vitesse-editable-description
      .value=${this.gatewayDescription}
      placeholder=${t('gateway.type')}
      hint=${t('dashboards.descriptionEditHint')}
      .canEdit=${canEdit}
      .commit=${(next: string) => this.commitGatewayDescription(next)}
    ></vitesse-editable-description>`;
  }
  protected renderBadges() {
    // Mirror the home gateway card: tri-state status (connecting → yellow,
    // connected → green, disconnected → gray) + the box's per-NIC IPs ALWAYS, and
    // — only when genuinely connected — the data-plane path (LAN vs private
    // server) + RTT and the PLC count (meaningless / stale otherwise).
    const st = this.eff();
    const via = st === 'connected' && this.connVia ? statusBadge('blue', connViaLabel(this.connVia)) : nothing;
    const plc = st === 'connected' ? statusBadge('blue', t('gateway.plcCount', { count: this.controllers.length })) : nothing;
    return html`${statusBadge(statusColor(st), statusText(st))}${via}${gatewayNetBadges(this.network)}${plc}`;
  }
  protected renderActions() {
    // The Connect/Disconnect/Cancel control always shows. The add-buttons are
    // FILTER-AWARE: the "all" filter shows both Add PLC/CNC + Add integration; the
    // controllers filter shows only Add PLC/CNC; the integrations filter shows only
    // Add integration; the other tabs show neither. (Add PLC/CNC also needs a
    // connected gateway + a plugin advertising manual-add — you can't add to a box
    // we're detached from.)
    const connected = this.eff() === 'connected';
    const canAddController = connected && this.pluginTypes.some((p) => p.canManuallyAdd);
    const showController = canAddController && (this.tab === 'all' || this.tab === 'controllers');
    const showIntegration = connected && (this.tab === 'all' || this.tab === 'integrations');
    // Gate the button that OPENS the dialog, not the dialog: letting someone fill
    // in a form they cannot submit is a worse refusal than not offering it.
    const addController = showController
      ? html`<vitesse-gated .rpc=${Plugins.method.addController} inline>
          ${button({ label: 'Add PLC or CNC', icon: 'plus', color: 'blue', onClick: () => (this.addOpen = true) })}
        </vitesse-gated>`
      : nothing;
    // The plan's ceilings, read BEFORE the click. The button locks only when
    // there is nothing left to add of ANY kind — one open kind keeps it live and
    // the picker inside greys the full ones. The lock names the first full kind
    // through the gate's quantitative axis, so its sentence comes from the same
    // `evaluate` every other lock uses. An absent ceiling is unlimited, never
    // zero (integration-limits.ts) — read it as zero and the button would lock
    // for exactly the tiers that bought the most.
    const s = getSession();
    const room = rooms(this.integrations, (f, k) => s?.limit(f, k));
    const full = everyKindFull(room) ? firstFull(room) : undefined;
    const addIntegration = showIntegration
      ? html`<vitesse-gated
          .rpc=${Integrations.method.apply}
          consumes=${full ? LIMIT_KEY[full.kind] : nothing}
          .used=${full?.used}
          inline
        >
          ${button({ label: 'Add integration', icon: 'plus', color: 'blue', onClick: () => (this.addIntegrationOpen = true) })}
        </vitesse-gated>`
      : nothing;
    // Kiosk lives HERE, with the other page actions — it acts on the gateway's
    // whole board set, exactly like the add-buttons act on its whole roster.
    // Filter-aware like them, and gated on having something to rotate through:
    // a rotation of one board is a still image.
    /*
     * CREATING A BOARD BY HAND. Until this existed, `dashboards.create` was reachable only as a
     * tool — the empty state told people to ask the assistant, which is a fine offer and a poor
     * requirement. Filter-aware like its neighbours, and gated on the same RPC it will call, so a
     * person who may not create one is not handed a form they cannot submit.
     */
    const createDashboard = this.tab === 'dashboards'
      ? html`<vitesse-gated .rpc=${Dashboards.method.create} inline>
          ${button({ label: t('dashboards.create'), icon: 'plus', color: 'blue', onClick: () => (this.createDashboardOpen = true) })}
        </vitesse-gated>`
      : nothing;
    const kiosk = this.tab === 'dashboards' && this.dashboards.length > 1
      ? button({
          label: t('dashboards.kiosk'),
          icon: 'monitor-play',
          color: 'blue',
          onClick: () =>
            this.dispatchEvent(
              new CustomEvent('kiosk-start', {
                detail: { gatewayId: this.gatewayId, dashboardId: this.dashboards[0].id },
                bubbles: true,
                composed: true,
              }),
            ),
        })
      : nothing;
    return html`${this.connButton()}${addController}${addIntegration}${createDashboard}${kiosk}`;
  }
  protected onBack(): void {
    this.nav({ view: 'home' });
  }
  protected backLabel(): string {
    return t('gateway.back');
  }
  protected backAriaLabel(): string {
    return t('gateway.ariaBack');
  }
  protected aiHints(): string[] {
    return [t('gateway.hintSummary'), t('gateway.hintIssues')];
  }
  protected renderContent() {
    // BasePage renders the shared pill-tab bar from tabs()/activeTabId()/onTabChange();
    // this is just the active tab's body + the (top-layer) add-controller dialog.
    return html`${this.content()}
      <vitesse-add-controller-dialog
        .open=${this.addOpen}
        .gatewayId=${this.gatewayId}
        .backend=${this.backend}
        .types=${this.pluginTypes}
        @dialog-close=${() => (this.addOpen = false)}
        @added=${() => (this.addOpen = false)}
      ></vitesse-add-controller-dialog>
      <vitesse-create-dashboard-dialog
        .open=${this.createDashboardOpen}
        .dashboards=${this.dashboardManager}
        .existingIds=${this.dashboards.map((d) => d.id)}
        @dialog-close=${() => (this.createDashboardOpen = false)}
        @created=${(e: CustomEvent<{ id: string }>) =>
          this.nav({ view: 'dashboard', gatewayId: this.gatewayId, dashboardId: e.detail.id })}
      ></vitesse-create-dashboard-dialog>
      <vitesse-add-integration-dialog
        .open=${this.addIntegrationOpen}
        .gatewayId=${this.gatewayId}
        .backend=${this.backend}
        .integrations=${this.integrations}
        @dialog-close=${() => (this.addIntegrationOpen = false)}
      ></vitesse-add-integration-dialog>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'vitesse-gateway-detail': GatewayDetail;
  }
}
